/**
 * Configuration Sentry pour le monitoring d'erreurs
 * Frontend React
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
const IS_PRODUCTION = ENVIRONMENT === 'production';

/**
 * Initialise Sentry pour le tracking d'erreurs
 * À appeler une seule fois au démarrage de l'application
 */
export function initSentry() {
  // Ne pas initialiser Sentry en développement sans DSN
  if (!SENTRY_DSN) {
    if (IS_PRODUCTION) {
      console.warn('⚠️  SENTRY_DSN non configuré en production');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Échantillonnage des traces de performance (10% en production)
    tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,

    // Replays de session (5% en production, 10% sur erreur)
    replaysSessionSampleRate: IS_PRODUCTION ? 0.05 : 0.1,
    replaysOnErrorSampleRate: IS_PRODUCTION ? 0.1 : 1.0,

    // Intégrations
    integrations: [
      // Replay de session pour debug
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),

      // Breadcrumbs pour React
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.netlify\.app/,
          // Ajouter votre domaine de production ici
        ],
      }),
    ],

    // Ne pas envoyer les erreurs de développement
    enabled: IS_PRODUCTION,

    // Filtrer les erreurs non pertinentes
    beforeSend(event, hint) {
      // Ne pas envoyer les erreurs réseau courantes
      const error = hint.originalException;

      if (error && error.message) {
        // Ignorer les erreurs de réseau communes
        if (
          error.message.includes('Failed to fetch') ||
          error.message.includes('Network request failed') ||
          error.message.includes('NetworkError')
        ) {
          return null;
        }

        // Ignorer les erreurs de CORS en développement
        if (!IS_PRODUCTION && error.message.includes('CORS')) {
          return null;
        }
      }

      return event;
    },

    // Tags par défaut
    initialScope: {
      tags: {
        'app.section': 'frontend',
      },
    },
  });

  console.log('✅ Sentry initialisé:', {
    environment: ENVIRONMENT,
    enabled: IS_PRODUCTION,
  });
}

/**
 * Capture une erreur manuellement
 * @param {Error} error - L'erreur à capturer
 * @param {Object} context - Contexte additionnel
 */
export function captureError(error, context = {}) {
  if (!SENTRY_DSN || !IS_PRODUCTION) {
    console.error('Error:', error, context);
    return;
  }

  Sentry.captureException(error, {
    contexts: {
      additional: context,
    },
  });
}

/**
 * Capture un message manuel
 * @param {string} message - Message à logger
 * @param {string} level - Niveau (info, warning, error)
 * @param {Object} context - Contexte additionnel
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!SENTRY_DSN || !IS_PRODUCTION) {
    console.log(`[${level}]`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    contexts: {
      additional: context,
    },
  });
}

/**
 * Définit l'utilisateur courant pour le contexte Sentry
 * @param {Object} user - Informations utilisateur
 */
export function setUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: `${user.prenom} ${user.nom}`,
    role: user.role,
  });
}

/**
 * Ajoute un breadcrumb (fil d'Ariane) pour debug
 * @param {string} message - Message du breadcrumb
 * @param {string} category - Catégorie (navigation, api, ui, etc.)
 * @param {Object} data - Données additionnelles
 */
export function addBreadcrumb(message, category = 'custom', data = {}) {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Démarre une span de performance (compatible Sentry v8+)
 * Note: Dans Sentry v8+, startSpan remplace startTransaction
 * @param {string} name - Nom de la span
 * @param {string} op - Type d'opération
 * @param {Function} callback - Fonction à exécuter dans le contexte de la span
 * @returns {any} Résultat du callback
 */
export function startTransaction(name, op = 'custom', callback = () => {}) {
  if (!SENTRY_DSN || !IS_PRODUCTION) {
    return callback();
  }

  // Utiliser startSpan pour Sentry v8+
  return Sentry.startSpan({ name, op }, callback);
}

export default Sentry;
